import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw, Timer, X } from "lucide-react";

const KEY = "xz-pomodoro";
const PRESETS = { focus: 25, short: 5, long: 15 };
const LABELS = { focus: "Focus", short: "Short break", long: "Long break" };

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s && typeof s === "object") return s;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
};
const save = (s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch (e) {
    /* ignore */
  }
};
const fmt = (ms) => {
  const t = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};
const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ac = new Ctx();
    const play = (f, at, dur) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.value = 0.0001;
      o.connect(g);
      g.connect(ac.destination);
      o.start(ac.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.2, ac.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + at + dur);
      o.stop(ac.currentTime + at + dur + 0.05);
    };
    play(880, 0, 0.18);
    play(1174, 0.22, 0.25);
  } catch (e) {
    /* audio blocked — fine */
  }
};

/**
 * Floating Pomodoro pill (bottom-center). State survives reloads via
 * localStorage; sessions are NOT stored in the cloud. Focus → short break,
 * every 4th focus → long break. `onFocusComplete` fires when a focus block ends.
 */
const Pomodoro = ({ onFocusComplete }) => {
  const [state, setState] = useState(() => {
    const s = load();
    return {
      mode: s?.mode || "focus",
      running: !!s?.running,
      endsAt: s?.endsAt || null,
      remainingMs: typeof s?.remainingMs === "number" ? s.remainingMs : PRESETS.focus * 60000,
      sessions: s?.sessions || 0,
      expanded: false,
    };
  });
  const [now, setNow] = useState(Date.now());
  const completeRef = useRef(onFocusComplete);
  completeRef.current = onFocusComplete;

  // Persist (except the transient `expanded` flag).
  useEffect(() => {
    const { expanded, ...rest } = state;
    save(rest);
  }, [state]);

  // Tick while running.
  useEffect(() => {
    if (!state.running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [state.running]);

  const remaining = state.running && state.endsAt ? state.endsAt - now : state.remainingMs;

  // Completion.
  useEffect(() => {
    if (!state.running || !state.endsAt || remaining > 0) return;
    const wasFocus = state.mode === "focus";
    const sessions = wasFocus ? state.sessions + 1 : state.sessions;
    const nextMode = wasFocus ? (sessions % 4 === 0 ? "long" : "short") : "focus";
    beep();
    setState((s) => ({
      ...s,
      mode: nextMode,
      running: false,
      endsAt: null,
      remainingMs: PRESETS[nextMode] * 60000,
      sessions,
      expanded: true,
    }));
    if (wasFocus && completeRef.current) completeRef.current();
  }, [remaining, state.running, state.endsAt, state.mode, state.sessions]);

  const start = () =>
    setState((s) => ({ ...s, running: true, endsAt: Date.now() + s.remainingMs }));
  const pause = () =>
    setState((s) => ({
      ...s,
      running: false,
      endsAt: null,
      remainingMs: Math.max(0, (s.endsAt || Date.now()) - Date.now()),
    }));
  const reset = (mode = state.mode) =>
    setState((s) => ({
      ...s,
      mode,
      running: false,
      endsAt: null,
      remainingMs: PRESETS[mode] * 60000,
    }));

  const total = PRESETS[state.mode] * 60000;
  const pct = Math.max(0, Math.min(100, 100 - (remaining / total) * 100));

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2">
      {state.expanded && (
        <div className="glass rounded-2xl card-shadow p-3 w-64" role="dialog" aria-label="Pomodoro timer">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-gray-400">
              {LABELS[state.mode]} · session {state.sessions + (state.mode === "focus" ? 1 : 0)}
            </span>
            <button
              type="button"
              onClick={() => setState((s) => ({ ...s, expanded: false }))}
              className="p-1 text-gray-400 hover:text-cyan-300"
              aria-label="Collapse timer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="font-display text-4xl text-cyan-100 text-center tabular-nums mb-2">
            {fmt(remaining)}
          </div>
          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden mb-3">
            <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-1 mb-2">
            {Object.keys(PRESETS).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => reset(m)}
                className={`text-[11px] px-2 py-1.5 rounded-lg font-semibold ${
                  state.mode === m
                    ? "bg-cyan-500/20 text-cyan-200"
                    : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {PRESETS[m]}m {LABELS[m].split(" ")[0]}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={state.running ? pause : start}
              className="tap flex-1 px-3 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white font-semibold text-sm flex items-center justify-center gap-1.5"
            >
              {state.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {state.running ? "Pause" : "Start"}
            </button>
            <button
              type="button"
              onClick={() => reset()}
              className="tap px-3 py-2 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600"
              aria-label="Reset timer"
              title="Reset"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setState((s) => ({ ...s, expanded: !s.expanded }))}
        className={`tap glass rounded-full pl-3 pr-2 py-1.5 card-shadow flex items-center gap-2 text-sm font-semibold ${
          state.running ? "text-cyan-200" : "text-gray-300"
        }`}
        aria-label={`Pomodoro ${LABELS[state.mode]} ${fmt(remaining)}`}
        title="Pomodoro timer"
      >
        <Timer className={`w-4 h-4 ${state.running ? "text-cyan-400" : "text-gray-400"}`} />
        <span className="tabular-nums">{fmt(remaining)}</span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            state.running ? pause() : start();
          }}
          className="p-1 rounded-full hover:bg-gray-700/60"
          role="button"
          aria-label={state.running ? "Pause" : "Start"}
        >
          {state.running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </span>
      </button>
    </div>
  );
};

export default Pomodoro;
